import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, payableInr } from "@/lib/api";
import { CheckCircle2, Download, LoaderCircle } from "lucide-react";
import { FoodSymbol } from "@/components/FoodSymbol";

const OrderSuccess = () => {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    api.get(`/orders/${id}`)
      .then((response) => {
        if (active) {
          setO(response.data);
        }
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (loadError) {
    return (
      <div className="px-4 py-20 text-center" data-testid="order-success-load-error">
        <h1 className="font-serif text-3xl font-semibold text-[#1E2A24]">Order unavailable</h1>
        <p className="mt-3 text-sm text-[#64748B]">
          We could not load this order. Please check your account for the latest status.
        </p>
        <Link
          to="/account?tab=orders"
          className="mt-6 inline-block rounded-lg bg-[#2D5A47] px-5 py-3 font-semibold text-white"
          data-testid="order-success-view-orders-link"
        >
          View orders
        </Link>
      </div>
    );
  }
  if (!o) {
    return <div className="py-20 text-center" data-testid="order-success-loading">Loading…</div>;
  }
  const inclusiveProducts = o.totals.inclusive_products ?? Math.max(
    o.totals.subtotal - o.totals.discount,
    0,
  );
  const productAmount = o.totals.subtotal ?? inclusiveProducts;
  const baseAmount = o.totals.base_amount ?? Number((inclusiveProducts / 1.18).toFixed(2));
  const gstAmount = o.totals.gst ?? Number((inclusiveProducts - baseAmount).toFixed(2));
  const downloadInvoice = async () => {
    if (downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const response = await api.get(`/orders/${o.id}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const filename = disposition.match(/filename="?([^";]+)"?/)?.[1];
      anchor.href = url;
      anchor.download = filename || `Eira-Products-Invoice-${o.id.slice(0, 8).toUpperCase()}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingInvoice(false);
    }
  };
  return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="order-success">
      <div className="text-center mb-10">
        <CheckCircle2 size={72} className="mx-auto text-[#2D5A47]" />
        <h1 className="font-serif text-4xl font-semibold mt-4">Order Confirmed</h1>
        <p className="text-[#64748B] mt-2">Thank you for shopping with Eira. Your order will be shipped soon.</p>
        <div className="inline-block mt-4 bg-[#F4F1EA] px-4 py-2 rounded-lg text-sm">Order ID: <span className="font-mono font-bold">{o.id.slice(0, 12)}</span></div>
      </div>
      <div className="bg-white border border-[#E2DCD2] rounded-xl p-6">
        <h2 className="font-serif text-2xl mb-4">Order Summary</h2>
        <div className="space-y-3 mb-4">
          {o.items.map((i, k) => (
            <div key={k} className="flex items-center gap-3 text-sm">
              <img src={i.image} className="w-12 h-12 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-1">
                  <FoodSymbol
                    value={i.food_symbol}
                    className="mt-0.5"
                    testId={`order-success-food-symbol-${o.id}-${k}`}
                  />
                  <span className="truncate">{i.name}</span>
                </div>
                <div className="text-xs text-[#64748B]">{i.variant_label} × {i.quantity}</div>
              </div>
              <div className="font-medium">{payableInr(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#E2DCD2] pt-4 space-y-1 text-sm">
          <div className="flex justify-between">
            <span>Product amount (inclusive GST)</span>
            <span>{payableInr(productAmount)}</span>
          </div>
          {o.totals.discount > 0 && (
            <div className="flex justify-between text-emerald-700">
              <span>Discount</span>
              <span>−{payableInr(o.totals.discount)}</span>
            </div>
          )}
          {o.totals.tier_discount > 0 && (
            <div className="flex justify-between text-[#2D5A47]">
              <span>{o.eira_loyalty_tier?.name || "Tier"} discount</span>
              <span>−{payableInr(o.totals.tier_discount)}</span>
            </div>
          )}
          {o.totals.points_discount > 0 && (
            <div className="flex justify-between text-[#2D5A47]">
              <span>Eira Points</span>
              <span>−{payableInr(o.totals.points_discount)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Base Amount</span>
            <span>{payableInr(baseAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span>GST (18%, included)</span>
            <span>{payableInr(gstAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping</span>
            <span>{o.totals.shipping ? payableInr(o.totals.shipping) : "Free"}</span>
          </div>
          <div className="mt-2 flex justify-between border-t border-[#E2DCD2] pt-2 text-lg font-bold">
            <span>Total</span>
            <span className="text-[#2D5A47]">{payableInr(o.totals.total)}</span>
          </div>
        </div>
      </div>
      <div className="text-center mt-8 flex gap-3 justify-center">
        <Link to="/shop" className="bg-[#2D5A47] text-white px-6 py-3 rounded-lg font-semibold">Continue Shopping</Link>
        <Link to="/account?tab=orders" className="bg-white border border-[#E2DCD2] px-6 py-3 rounded-lg font-semibold">View Orders</Link>
        <button
          onClick={downloadInvoice}
          disabled={downloadingInvoice}
          aria-busy={downloadingInvoice}
          className={`px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition-colors ${
            downloadingInvoice
              ? "bg-[#2D5A47] border border-[#2D5A47] text-white cursor-wait"
              : "bg-white border border-[#E2DCD2] hover:border-[#2D5A47]"
          }`}
          data-testid="download-order-invoice"
        >
          {downloadingInvoice ? <LoaderCircle size={16} className="animate-spin" /> : <Download size={16} />}
          {downloadingInvoice ? "Preparing invoice…" : "Invoice PDF"}
        </button>
      </div>
    </div>
  );
};

export default OrderSuccess;
